import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  Signal,
  signal
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { HeaderAndFooterResponseInterface } from "@gea-waldviertler-website/gea-models";
import { AuthService } from "../services/auth.service";
import {
  BreadcrumbButtonInterface,
  BreadcrumbPathItem,
  BreadcrumbService,
  PageService
} from "@gea-waldviertler-website/shop-lib";
import { CartService } from "../services/cart.service";
import { isPlatformBrowser, Location } from '@angular/common';
import { ElasticsearchService } from "../search/elasticsearch.service";

@Component({
  selector: 'gea-shop-navigation',
  templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class NavigationComponent implements OnInit {

  // if null, hide breadcrumbs
  breadcrumbs: Signal<BreadcrumbPathItem[] | null> = computed(() => {
    const before = this.bcService.getBreadcrumbSignal()()
    if (! before) return null
    let bc = [ ... before ]
    bc.unshift({ type: 'shop-home', link: '/', label: "Shop" } as BreadcrumbPathItem)
    return bc
  })

  buttons: Signal<BreadcrumbButtonInterface[] | undefined> = computed(() => {
    return this.bcService.getButtonSignal()()
  })

  inShop = computed<boolean>(() => {
    return true
  })

  cartItemsCount = computed<number>(() => {
    return this.cartService.cart().items.length
  })

  public mainCategories = computed(() => {
    const bs = this.pageService.getBootstrapSignal()()
    if (bs) {
      return bs.categories
    }
    return undefined
  })

  isMenuCollapsed: boolean = true
  isMegaMenuCollapsed = signal<boolean>(true)
  currentRouteSegment = signal<string>('')

  cmsResponse = input<HeaderAndFooterResponseInterface | undefined>(undefined)

  private platformId = inject(PLATFORM_ID)
  public route = inject(ActivatedRoute)
  public bcService = inject(BreadcrumbService)
  public authService = inject(AuthService)
  public cartService = inject(CartService)
  public pageService = inject(PageService)
  public es = inject(ElasticsearchService)

  constructor(
    private cd: ChangeDetectorRef,
    private router: Router,
    private location: Location,
    private renderer: Renderer2
  ) {

    // React to breadcrumb/button signal changes to keep view in sync
    effect(() => {
      // touch the signals so this effect re-runs when they change
      this.bcService.getBreadcrumbSignal()()
      this.bcService.getButtonSignal()()
      // schedule a view refresh after state updates settle
      this.refreshView()
    })
  }

  isBrowser() {
    return isPlatformBrowser(this.platformId)
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      let url = new URL(window.location.href)
      for (const urlElement of url.searchParams) {
        if (urlElement.length == 2 && urlElement[0] == "loginToken") {
          this.authService.loginWithHash(urlElement[1])
        }
      }
    }

    this.location.onUrlChange(() => {
      // make sure mega menu is collapsed
      this.isMegaMenuCollapsed.set(true)
      if (isPlatformBrowser(this.platformId)) {
        this.renderer.removeClass(document.body, 'noscroll')
      }
      this.es.getToggleSearchSubject().next(false)
      // defer to next tick so any services (e.g., BreadcrumbService) finish updating first
      setTimeout(() => this.refreshView())
    })

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentRouteSegment.set(event.url)
        // ensure view updates after navigation completed
        this.refreshView()
      }
    })

    this.es.getCmsSearchResults().subscribe(r => { this.refreshView() })
    this.es.getProductCollection().subscribe(r => { this.refreshView() })
  }

  refreshView() {
    // Schedule a change detection pass at the end of the current macrotask
    // to ensure services (like BreadcrumbService) have updated.
    setTimeout(() => {
      try {
        this.cd.detectChanges()
      } catch {}
    }, 0)
  }

  /**
   * close megamenu and search when menu item is clicked
   */
  onIconClick() {
    this.isMegaMenuCollapsed.set(true)
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.removeClass(document.body, 'noscroll')
    }
    this.es.getToggleSearchSubject().next(false)
    this.refreshView()
  }

  onToggle() {
    this.isMegaMenuCollapsed.update(collapsed => ! collapsed)
    if (this.isMegaMenuCollapsed() && isPlatformBrowser(this.platformId)) {
      this.renderer.removeClass(document.body, 'noscroll')
    } else {
      this.renderer.addClass(document.body, 'noscroll')
    }
  }

}
